import {observer} from 'mobx-react-lite';
import {useService} from '../../../../infrastructure/service/service';
import React, {useEffect, useState} from 'react';
import {ValidateError} from '../../../../entity/resource/validate-error';
import {Button, Card, CardContent, LinearProgress, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import {ValidateDocumentProps} from '../../../../entity/props/title/validate-document-props';
import {toast} from 'react-toastify';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import ValidateDocumentDialog from './ValidateDocumentDialog';
import {Title} from '../../../../entity/model/title';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/validate-document.css';

const ValidateDocuments = (props: ValidateDocumentProps): JSX.Element => {
  const {titleService, documentService, auditLogsService} = useService();

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [validationResults, setValidationResults] = useState<ValidateError[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
  const [batchId, setBatchId] = useState<string>('');
  const [lastStateMessage, setLastStateMessage] = useState<string>('');
  const [openValidateDialog, setOpenValidateDialog] = useState<boolean>(false);
  const [title, setTitle] = useState<Title>();

  useEffect(() => {
    setTitle(titleService.getTitle());
  });

  const renderValidationComponent = (validateError: ValidateError, index: number): JSX.Element => {
    return (
      <Grid size={{xs: 12, sm: 12, md: 6, lg: 4, xl: 4}} key={index}>
        <Box className={'validation-component'}>
          <Typography className={'validation-document-name'}>{validateError.doc.name}</Typography>
          <Typography className={'validation-document-id'}>{validateError.doc.id}</Typography>
          <Link className={'validation-document-url'} href={validateError.doc.url} target='_blank' rel='noopener noreferrer'>Document URL</Link>
          <Typography className={'validation-document-error'}>{validateError.error}</Typography>
        </Box>
      </Grid>
    );
  }

  const handleValidate = async (documentIds: string[]): Promise<void> => {
    setSubmitLoading(true);
    setOpenValidateDialog(false);
    setProgress(5);
    setMessage('Your request is being processed');

    const batchId = crypto.randomUUID();
    documentService.validateDocument(props.projectTitle.createdByOrganizationID, title?.id ?? '', documentIds, batchId);

    await fetchProgress(batchId);
  }

  const fetchProgress = async (batchId: string): Promise<void> => {
    let previousProgress = 0;
    let progressUnchangedDuration = 0;
    const maxUnchangedDuration = 20000;
    const interval = 5000;
    const maxUnchangedCycles = maxUnchangedDuration / interval;

    const intervalId = setInterval(async (): Promise<void> => {
      const response = await auditLogsService.getValidateDocumentLogs(props.projectTitle.createdByOrganizationID, batchId);
      if (response) {
        const progressStatus = response.progressStatusDto;
        const validateResponse = response.validateResponse;

        if (progressStatus.progress > progress)
          setProgress(progressStatus.progress);

        setMessage(progressStatus.message);

        if (progressStatus.progress === 100) {
          setValidationResults(validateResponse?.errorDocuments ?? []);

          toast.success(progressStatus.message);
          setTimeout((): void => setSubmitLoading(false), 1000);
          clearInterval(intervalId);
        }

        if (progressStatus.progress !== previousProgress) {
          previousProgress = progressStatus.progress;
          progressUnchangedDuration = 0;
        } else
          progressUnchangedDuration++;

        if (progressUnchangedDuration >= maxUnchangedCycles) {
          setProgressRefresh(true);
          setBatchId(batchId);
          setLastStateMessage(progressStatus.message);

          setMessage('Validating your PDFs. Please refresh to check the progress.');
          toast.info('Validating your PDFs. Please refresh to check the progress.');
          clearInterval(intervalId);
        }
      }
    }, interval);
  }

  const handleProgressRefresh = async (): Promise<void> => {
    setProgressRefresh(false);
    setMessage(lastStateMessage);
    setProgress((prevState: number): number => prevState + 1);

    await fetchProgress(batchId);
  }

  return (
    <>
      <Box className={'body-box'}>
        <Typography className={'document-tab-title'}>Validate Documents</Typography>
        <Card elevation={0} className={'validate-document-card'}>
          <CardContent>
            <Box>
              <Typography className={'validation-result-title'}>Validation Results ({validationResults.length})</Typography>
              {
                validationResults.length === 0
                  ? <Typography className={'table-cell-text'}>No Errors</Typography>
                  : <Grid container spacing={2}>
                      {
                        validationResults.map(renderValidationComponent)
                      }
                    </Grid>
              }
            </Box>
          </CardContent>
        </Card>
        <Box className={'validate-zone'}>
          {
            submitLoading
              ? <Box className={'progress-zone'}>
                  <Box className={'progress-icon-zone'}>
                    {
                      progressRefresh
                        ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                        : <InsertDriveFileIcon className={'progress-icon'} />
                    }
                  </Box>
                  <Box className={'progress-bar-zone'}>
                    <Typography className={'progress-bar-title'}>{message}</Typography>
                    <Box className={'progress-bar-container'}>
                      <Box className={'progress-bar'}>
                        <LinearProgress variant='determinate' {...props} value={progress} color='success' className={'progress'}/>
                      </Box>
                      <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                    </Box>
                  </Box>
                </Box>
              : <>
                  <Typography className={'validate-text'}>Please click the validate button to proceed</Typography>
                  <Button onClick={(): void => setOpenValidateDialog(true)} disableRipple className={'zitles-button submit'}>Validate</Button>
                </>
          }
        </Box>
      </Box>
      {
        openValidateDialog && title &&
          <ValidateDocumentDialog
            open={openValidateDialog}
            setOpen={setOpenValidateDialog}
            selectedTitle={title}
            handleSubmit={handleValidate}/>
      }
    </>
  );
}

export default observer(ValidateDocuments);
